import classNames from "classnames";
import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { ArrowDropDownIcon, FlexContent, Tabs } from "@rothoblaas/design-system";
import { resolveAccentColor } from "../../../constants/colors";
import useI18n from "../../../hooks/useTranslations";
import {
  DEFAULT_JOIST_TOP_ALIGNMENT,
  type Configuration,
  LockCalculatorDefinition,
  LockCalculatorDefinitionUpdateData,
  type OperationMode,
} from "../../../http/lock_calculator/types";
import { Calculation, UpdateCalculationBody } from "../../../http/types/calculations";
import { useCalculationsApi } from "../../../http/useCalculations";
import ExpansionPanel from "../../../components/ui/ExpansionPanel";
import styles from "./LockCalculator.module.css";
import { HelpModal, type HelpContent } from "./components/HelpModal";
import { fromCalculationData, toCalculationUpdateData } from "./data/calculationAdapter";
import { useFirstLockFamily, useLockCatalogEntry } from "./hooks/useFirstLockFamily";
import { isHeaderLockApplicable } from "./model/headerLock";
import { buildLockCalculatorDefaults, useAutoSaveLockCalculator } from "./data/useAutoSaveLockCalculator";
import { BomSection } from "./features/bom/BomSection";
import { LockCalculator3DViewer } from "./features/preview/LockCalculator3DViewer";
import { PdfReportSection } from "./features/report/PdfReportSection";
import { LockSceneCapture, type OrthographicViews } from "./3d/LockSceneCapture";
import { GeneratedConfigurationsTable } from "./features/results/GeneratedConfigurationsTable";
import { ResultsSection } from "./features/results/ResultsSection";
import { buildCascadeRecord } from "./features/notices/applyCascade";
import { STEP_TRANSITION_MS, flashNoticeField, focusNoticeTarget, withReachableField } from "./features/notices/focusNoticeTarget";
import { createNoticeTargets } from "./features/notices/noticeTargets";
import { NoticesPanel } from "./features/notices/NoticesPanel";
import { NoticesProvider, useCalculatorNotices } from "./features/notices/NoticesContext";
import { CalculatorWizard } from "./features/wizard/CalculatorWizard";
import { LockCalculatorCatalogProvider } from "./hooks/useLockCalculatorCatalog";
import { useLockCalculatorFormRules } from "./hooks/useLockCalculatorFormRules";
import { useLockCalculatorResults } from "./hooks/useLockCalculatorResults";
import { useLockCalculatorWizard } from "./hooks/useLockCalculatorWizard";
import type { CalculationCoefficients, LockCalculatorFormValues } from "./model/form";
import { explainsEmptyGeneration, type NoticeCode } from "./model/noticeCatalog";
import { fromBackendNotices, noticeStep, noticesShownAtField, type CalculatorNotice, type CascadeRecord } from "./model/notices";
import { buildCalculationLegend } from "./model/calculationLegend";
import { RESULTS_TAB, RESULTS_TAB_PARAMETER } from "./model/resultsTabs";
import { connectorCode } from "./model/connectorCode";
import { toLockSceneInput, type ConnectorCodes, type FastenerSpecs, type TopDistanceBase } from "./model/toLockSceneInput";
export type LockCalculatorProps = {
  data?: Calculation<LockCalculatorDefinition>;
  isAdmin?: boolean;
  readOnly?: boolean;
};

/** Attesa (ms) a scena 3D ferma prima di rifare la capture del report. */
const CAPTURE_DEBOUNCE_MS = 800;

type FollowedDefault = { value: number | null; prevDefault: number | null; nextDefault: number | null };

/** Keeps a numeric field on its backend default: an empty field, or one still equal to the previous
 * default, takes the new one; a typed value is never overwritten. No new default drops an old one.
 * `onFollow` is told when a filled value moved with its default. */
const syncFollowingDefault = (
  form: UseFormReturn<LockCalculatorFormValues>,
  valueField: "joistMember.connectorTopDistance" | "joistMember.topOffsetFromHeader",
  defaultField: "joistMember.connectorTopDistanceDefault" | "joistMember.topOffsetDefault",
  { value, prevDefault, nextDefault }: FollowedDefault,
  onFollow?: (before: number, after: number) => void,
) => {
  const followsDefault = value == null || value === prevDefault;
  if (nextDefault == null) {
    if (prevDefault != null && value === prevDefault) {
      form.setValue(valueField, null);
      form.setValue(defaultField, null);
    }
    return;
  }
  if (followsDefault && value !== nextDefault) {
    form.setValue(valueField, nextDefault);
    if (value != null) onFollow?.(value, nextDefault);
  }
  if (prevDefault !== nextDefault) form.setValue(defaultField, nextDefault);
};

const LockCalculator: FC<LockCalculatorProps> = ({ data, isAdmin = false, readOnly = false }) => {
  const i18n = useI18n();
  const t = i18n.translation;
  const defaultValues = useMemo<LockCalculatorFormValues>(() => buildLockCalculatorDefaults(data), [data]);

  const formMethods = useForm<LockCalculatorFormValues>({
    defaultValues,
    mode: "onChange",
  });

  // Reset form only when switching to a different calculation, not on every auto-save response
  useEffect(() => {
    formMethods.reset(buildLockCalculatorDefaults(data));
  }, [data?.id, formMethods]);

  const { lockCalculatorCalculation, applyExternalUpdate, hasUnsavedChanges } = useAutoSaveLockCalculator(
    data,
    formMethods.control,
    isAdmin,
  );
  const watchedValues = useWatch({ control: formMethods.control });
  // Coefficients resolved by the backend on save, shown in the code/norm step and read by the notices.
  const coefficients = useMemo<CalculationCoefficients>(
    () => ({
      kmod: lockCalculatorCalculation?.kmod ?? null,
      gammaM: lockCalculatorCalculation?.gammaMConnection ?? null,
      gammaMAluminium: lockCalculatorCalculation?.gammaMAluminium ?? null,
      kmodDefault: lockCalculatorCalculation?.kmodDefault ?? null,
      gammaMDefault: lockCalculatorCalculation?.gammaMConnectionDefault ?? null,
      gammaMAluminiumDefault: lockCalculatorCalculation?.gammaMAluminiumDefault ?? null,
    }),
    [lockCalculatorCalculation],
  );
  // One registry for every notice: backend state, form state, the cascades the rules applied, staleness.
  const [noticeTargets] = useState(createNoticeTargets);
  const { notices, automaticBlock, reportCascade: recordCascade, markProduced } = useCalculatorNotices({
    values: watchedValues as LockCalculatorFormValues,
    initialValues: defaultValues,
    calculation: lockCalculatorCalculation,
    coefficients,
    calculationId: data?.id,
  });
  // A rule that changes a field on its own also makes that field flash, so the change is not missed.
  const reportCascade = useCallback(
    (record: CascadeRecord) => {
      recordCascade(record);
      window.requestAnimationFrame(() => flashNoticeField(record.effect.path, noticeTargets));
    },
    [recordCascade, noticeTargets],
  );
  // Real fastener dimensions for the Structure step's 3D preview — single
  // source of truth is the loaded Configuration's own Fastener items (same
  // catalog data the BOM/results already use), not a separate fetch.
  const fastenerSpecs = useMemo<FastenerSpecs>(() => {
    const configuration = lockCalculatorCalculation?.configuration;
    const spec = (item?: { length: number; diameter: number; item?: { code?: string | null } } | null) => ({
      lengthMm: item?.length ?? null,
      diameterMm: item?.diameter ?? null,
      code: item?.item?.code ?? null,
    });
    return {
      header: spec(configuration?.headerFastenerItem),
      joist: spec(configuration?.joistFastenerItem),
      inclined: spec(configuration?.inclinedFastenerItem),
    };
  }, [lockCalculatorCalculation]);
  // Family of the first connector's lock, from the lock catalog (the saved calculation can return it empty);
  // undefined until the catalog has loaded.
  const firstLockFamily = useFirstLockFamily(watchedValues?.connectorPairs?.[0]?.lockItemId, isAdmin);
  // Code of the wider header lock for the 3D legend (same catalog source as the family above).
  const headerLockCode = useLockCatalogEntry(watchedValues?.bom?.headerLockItemId, isAdmin)?.description ?? null;
  // The wider header lock only exists for a MIDI lock under a downward vertical load with a beam header: drop the
  // choice as soon as that stops holding, whichever tab is open, so BOM, 3D and the saved value stay consistent.
  const verticalLoad = watchedValues?.loads?.vertical;
  const headerOrientation = watchedValues?.headerMember?.orientation;
  useEffect(() => {
    if (firstLockFamily === undefined || isHeaderLockApplicable(verticalLoad, firstLockFamily, headerOrientation)) return;
    if (formMethods.getValues("bom.headerLockItemId")) {
      formMethods.setValue("bom.headerLockItemId", null, { shouldDirty: true });
    }
  }, [firstLockFamily, verticalLoad, headerOrientation, formMethods]);
  // Catalog code of each connector pair for the 3D viewer's legend: the EVO code when the finish is EVO,
  // like the BOM and the results table (connectorCode), from the same Configuration.connectors.
  const connectorCodes = useMemo<ConnectorCodes>(() => {
    const connectors = lockCalculatorCalculation?.configuration?.connectors ?? [];
    return [connectors[0] ? (connectorCode(connectors[0]) ?? null) : null, connectors[1] ? (connectorCode(connectors[1]) ?? null) : null];
  }, [lockCalculatorCalculation]);
  // EC5 service class for the 3D preview's environment/legend (28-09-2026) —
  // same already-resolved ServiceClass the environment step's form reads, no
  // separate catalog fetch. `label` is the SAME translated string as the
  // environment step's field (t.lockCalculatorModule.env.serviceClass), not
  // hardcoded here — the 3D subsystem has no i18n of its own (SPEC §6
  // boundary), so the caller resolves the text.
  const serviceClass = useMemo(() => {
    const sc = lockCalculatorCalculation?.serviceClass;
    if (!sc) return null;
    const label = t.lockCalculatorModule?.env?.serviceClass ?? t.lockCalculatorModule?.connection?.serviceClass;
    return { classNumber: sc.classNumber, label: label ?? "Service class" };
  }, [lockCalculatorCalculation, t]);
  // Legend rows on the calculation settings: the labels are the ones the form steps and the notices already
  // use, so nothing is translated twice.
  const calculationLegend = useMemo(() => {
    const module = t.lockCalculatorModule;
    return buildCalculationLegend(
      lockCalculatorCalculation,
      {
        standard: module.env.standard,
        loadDurationClass: module.env.loadDurationClass,
        kmod: module.notices.coefficients.kmod,
        gammaM: module.notices.coefficients.gammaM,
        gammaMAluminium: module.notices.coefficients.gammaMAluminium,
        custom: module.env.customCoefficients,
        headerMaterial: module.timber.headerMaterialGrade,
        joistMaterial: module.timber.joistMaterialGrade,
        combined: module.combined.title,
      },
      t.languageCode,
    );
  }, [lockCalculatorCalculation, t]);
  // Legend rows for pre-drilling: the backend decides (ADR-025), here only the
  // translated text; a member the backend could not decide gets no row.
  const preDrillingLines = useMemo<string[]>(() => {
    const legend = t.lockCalculatorModule.preDrillingLegend;
    const line = (label: string, preDrilled: boolean | null | undefined) =>
      preDrilled == null ? [] : [`${label}: ${preDrilled ? legend.yes : legend.no}`];
    return [
      ...line(legend.header, lockCalculatorCalculation?.headerPreDrilled),
      ...line(legend.joist, lockCalculatorCalculation?.joistPreDrilled),
    ];
  }, [lockCalculatorCalculation, t]);
  // Dati di catalogo del lockstop scelto (28-09-2026) — dalla Configuration
  // GIA' CARICATA, non dal form live (vedi doc-comment su
  // LockSceneProps.lockstopInstallationLevels per il perche').
  const lockstopInstallationLevels = lockCalculatorCalculation?.configuration?.installationLevels ?? null;
  const lockstopCode = lockCalculatorCalculation?.configuration?.lockstopItem?.item.code ?? null;
  const screwEdgeThicknessMm =
    lockCalculatorCalculation?.configuration?.connectors?.[0]?.lockItem?.screwEdgeThickness ?? null;
  // Backend defaults for the connector distance / joist offset when left empty.
  const topDistanceDefault = lockCalculatorCalculation?.connectorTopDistanceDefault ?? null;
  const topOffsetDefault = lockCalculatorCalculation?.joistTopOffsetDefault ?? null;
  const topDistanceBase = useMemo<TopDistanceBase>(
    () => ({ distanceDefault: topDistanceDefault, offsetDefault: topOffsetDefault }),
    [topDistanceDefault, topOffsetDefault],
  );
  const { updateCalculation } = useCalculationsApi();
  // Single source of truth for "send a partial update and adopt the response
  // into the form" — chooseConfiguration and triggerOperation only differ in
  // what `data` they send.
  const sendUpdate = useCallback(
    async (updateData: Partial<LockCalculatorDefinitionUpdateData>) => {
      if (!data) return;
      const body: UpdateCalculationBody<LockCalculatorDefinitionUpdateData> = {
        name: data.name,
        description: data.description ?? null,
        data: updateData,
      };
      const updated = await updateCalculation(data.project?.id, data.id, body, data, isAdmin);
      applyExternalUpdate(updated);
      formMethods.reset(fromCalculationData(updated.data));
      return updated;
    },
    [data, isAdmin, updateCalculation, applyExternalUpdate, formMethods],
  );
  // The 3D preview closes while the generated-configurations table is shown
  // (more room for it) and reopens once a configuration is loaded.
  const [isPreviewExpanded, setIsPreviewExpanded] = useState(true);
  const chooseConfiguration = useCallback(
    async (configurationId: string) => {
      const updated = await sendUpdate({ selectedConfigurationId: configurationId });
      if (updated) markProduced({ result: true }, fromCalculationData(updated.data));
      setIsPreviewExpanded(true);
    },
    [sendUpdate, markProduced],
  );
  const [isTriggeringOperation, setIsTriggeringOperation] = useState(false);
  const triggerOperation = useCallback(
    async (operationMode: OperationMode) => {
      setIsTriggeringOperation(true);
      try {
        const updated = await sendUpdate(toCalculationUpdateData(formMethods.getValues(), operationMode));
        if (updated) {
          const form = fromCalculationData(updated.data);
          if (operationMode === "generateManual") {
            markProduced({ result: true }, form);
          } else {
            const noCandidate = fromBackendNotices(updated.data?.notices).find((notice) => explainsEmptyGeneration(notice.code));
            // The reason shows in the notices panel: a backend one, or noDraftWithinLimits.
            markProduced({ drafts: operationMode, generationEmpty: !updated.data?.draftConfigurationsTotal && !noCandidate }, form);
          }
        }
        if (operationMode !== "generateManual" && updated?.data?.draftConfigurations?.length) {
          setIsPreviewExpanded(false);
        }
      } finally {
        setIsTriggeringOperation(false);
      }
    },
    [sendUpdate, formMethods, markProduced],
  );
  // Keep "Connector distance from joist top" and "Joist top offset" filled with
  // the backend defaults: only an empty field or one still equal to the previous
  // default is overwritten, never a typed value. The defaults depend on the
  // chosen lock, so with no lock selected they are dropped and the fields stay
  // empty (clearing a field in TimberForm drops the lock too, see there).
  const orientation = watchedValues.headerMember?.orientation ?? null;
  const topAlignment = watchedValues.joistMember?.topAlignment ?? DEFAULT_JOIST_TOP_ALIGNMENT;
  const topDistanceValue = watchedValues.joistMember?.connectorTopDistance ?? null;
  const topDistancePrevDefault = watchedValues.joistMember?.connectorTopDistanceDefault ?? null;
  const topOffsetValue = watchedValues.joistMember?.topOffsetFromHeader ?? null;
  const topOffsetPrevDefault = watchedValues.joistMember?.topOffsetDefault ?? null;
  const hasLockSelected = watchedValues.connectorPairs?.[0]?.lockItemId != null;
  const nextDistanceDefault = hasLockSelected ? (topDistanceDefault ?? (orientation === "column" ? null : 0)) : null;
  const nextOffsetDefault = orientation === "column" && topAlignment === "offset" && hasLockSelected ? topOffsetDefault : null;
  useEffect(() => {
    if (readOnly || orientation == null) return;
    const followed = (code: NoticeCode, effect: "joistMember.connectorTopDistance" | "joistMember.topOffsetFromHeader") =>
      (before: number, after: number) =>
        reportCascade(
          buildCascadeRecord(formMethods, {
            code,
            causes: ["connectorPairs.0.lockItemId", "headerMember.orientation", "joistMember.topAlignment"],
            effect,
            next: after,
            params: { before, after },
          }),
        );
    syncFollowingDefault(
      formMethods,
      "joistMember.connectorTopDistance",
      "joistMember.connectorTopDistanceDefault",
      { value: topDistanceValue, prevDefault: topDistancePrevDefault, nextDefault: nextDistanceDefault },
      followed("topDistanceFollowedDefault", "joistMember.connectorTopDistance"),
    );
    syncFollowingDefault(
      formMethods,
      "joistMember.topOffsetFromHeader",
      "joistMember.topOffsetDefault",
      { value: topOffsetValue, prevDefault: topOffsetPrevDefault, nextDefault: nextOffsetDefault },
      followed("topOffsetFollowedDefault", "joistMember.topOffsetFromHeader"),
    );
  }, [
    reportCascade,
    readOnly,
    orientation,
    topDistanceValue,
    topDistancePrevDefault,
    nextDistanceDefault,
    topOffsetValue,
    topOffsetPrevDefault,
    nextOffsetDefault,
    formMethods,
  ]);
  useLockCalculatorFormRules(formMethods, watchedValues as LockCalculatorFormValues, reportCascade);
  const { hasActiveCombination, hasResults } = useLockCalculatorResults(
    watchedValues as LockCalculatorFormValues,
    lockCalculatorCalculation,
    t,
  );

  // ── Comparison (Generated configurations → "Compare" → Results tab) ──
  const [comparisonConfigurations, setComparisonConfigurations] = useState<Configuration[]>([]);
  const [, setSearchParams] = useSearchParams();
  const handleCompare = useCallback(
    (selected: Configuration[]) => {
      setComparisonConfigurations(selected.slice(0, 5));
      setSearchParams((params) => {
        const next = new URLSearchParams(params);
        next.set(RESULTS_TAB_PARAMETER, String(RESULTS_TAB.calculationComparison));
        return next;
      });
    },
    [setSearchParams],
  );
  const handleRemoveComparison = useCallback(
    (id: string) => setComparisonConfigurations((prev) => prev.filter((configuration) => configuration.id !== id)),
    [],
  );

  // ── 3D capture for PDF report ──
  // La capture va rifatta a OGNI cambio della scena 3D, non solo di calcolo o
  // configurazione (bug 30-09-2026: cambiando orientamento header o α/β il
  // viewer si aggiornava, le immagini del report no, fino all'F5). La firma e'
  // lo stesso input del viewer (`toLockSceneInput`) piu' i dati lockstop.
  // Invalidazione immediata (niente report con immagini vecchie), nuova
  // cattura solo quando la firma e' ferma da CAPTURE_DEBOUNCE_MS: mentre si
  // digita non si ricrea un contesto WebGL a ogni tasto.
  //
  // La capture NON parte da sola: la richiede la sezione Report al clic su
  // Genera/Scarica (`captureRequested`) e si spegne appena le viste arrivano.
  // Montarla a ogni modifica (un secondo canvas WebGL con tutta la scena) costava
  // memoria e faceva lampeggiare la sezione Report a ogni variazione dei campi.
  const [capturedViews, setCapturedViews] = useState<OrthographicViews | null>(null);
  const [captureRequested, setCaptureRequested] = useState(false);
  const handleCaptured = useCallback((views: OrthographicViews) => {
    setCapturedViews(views);
    setCaptureRequested(false);
  }, []);
  const requestCapture = useCallback(() => setCaptureRequested(true), []);
  const configId = lockCalculatorCalculation?.configuration?.id;
  const sceneSignature = useMemo(
    () =>
      JSON.stringify([
        data?.id ?? null,
        configId ?? null,
        watchedValues
          ? toLockSceneInput(watchedValues as LockCalculatorFormValues, fastenerSpecs, undefined, topDistanceBase)
          : null,
        lockstopInstallationLevels,
        lockstopCode,
        screwEdgeThicknessMm ?? null,
      ]),
    [data?.id, configId, watchedValues, fastenerSpecs, topDistanceBase, lockstopInstallationLevels, lockstopCode, screwEdgeThicknessMm],
  );
  const [captureSignature, setCaptureSignature] = useState(sceneSignature);
  useEffect(() => {
    setCapturedViews(null);
    const timer = window.setTimeout(() => setCaptureSignature(sceneSignature), CAPTURE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [sceneSignature]);

  // ── Wizard stepper (moved before viewerParams) ──
  const { currentStep, goToStep } = useLockCalculatorWizard();

  // Il form resta montato anche da collassato (lo nasconde il CSS): smontarlo
  // rimonterebbe wizard e campi a ogni apertura.
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  // "Go to": open the step that owns the notice, then focus and flash its target once the step is shown.
  const goToTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(goToTimer.current), []);
  const goToNotice = useCallback(
    (notice: CalculatorNotice) => {
      const reachable = withReachableField(notice, noticeTargets);
      setIsFormCollapsed(false);
      goToStep(noticeStep(reachable));
      window.clearTimeout(goToTimer.current);
      goToTimer.current = window.setTimeout(() => focusNoticeTarget(reachable, noticeTargets), STEP_TRANSITION_MS);
    },
    [goToStep, noticeTargets],
  );
  // The report is built from the saved state: the inputs in force when it starts are what it will show.
  const reportSnapshot = useRef<LockCalculatorFormValues | null>(null);
  const handleReportStart = useCallback(() => {
    reportSnapshot.current = structuredClone(formMethods.getValues());
  }, [formMethods]);
  const handleReportGenerated = useCallback(() => {
    if (reportSnapshot.current) markProduced({ report: true }, reportSnapshot.current);
  }, [markProduced]);
  const noticesContext = useMemo(
    () => ({
      notices,
      reportCascade,
      noticesForField: (path: string) => noticesShownAtField(notices, path),
      goToNotice,
      registerTarget: noticeTargets.register,
      automaticBlock,
    }),
    [notices, reportCascade, goToNotice, noticeTargets, automaticBlock],
  );

  const accentColor = resolveAccentColor(data?.module?.category?.color);

  // ── Shared help modal ──
  const [helpModal, setHelpModal] = useState<HelpContent | null>(null);
  const onOpenHelp = useCallback((text: string, image?: string) => setHelpModal({ text, image }), []);

  // Inject accentColor into modal-root for portal-rendered modals
  useEffect(() => {
    const root = document.getElementById("modal-root");
    if (root && accentColor) root.style.setProperty("--accent-base", accentColor);
  }, [accentColor]);

  // Il conteggio nel titolo e' il totale generato; la tabella ne mostra solo i
  // migliori (draftConfigurations, limitato dal server).
  const generatedCount =
    lockCalculatorCalculation?.draftConfigurationsTotal ?? lockCalculatorCalculation?.draftConfigurations?.length ?? 0;

  const contents = [
    <GeneratedConfigurationsTable
      calculation={lockCalculatorCalculation}
      onSelect={chooseConfiguration}
      onCompare={handleCompare}
    />,
    <ResultsSection
      calculation={lockCalculatorCalculation}
      comparisonConfigurations={comparisonConfigurations}
      onRemoveComparison={handleRemoveComparison}
      loads={(watchedValues as LockCalculatorFormValues | undefined)?.loads}
      hasActiveCombination={hasActiveCombination}
      readOnly={readOnly}
      onOpenHelp={onOpenHelp}
      t={t}
    />,
    <BomSection
      items={lockCalculatorCalculation?.billOfMaterials}
      readOnly={readOnly}
      isAdmin={isAdmin}
      onOpenHelp={onOpenHelp}
    />,
    <PdfReportSection
      calculationId={data?.id}
      moduleName={data?.module?.name}
      calculationName={data?.name}
      projectName={data?.project?.name}
      enabled={hasResults}
      capturedViews={capturedViews}
      hasUnsavedChanges={hasUnsavedChanges}
      onGenerateStart={handleReportStart}
      onGenerated={handleReportGenerated}
      onRequestCapture={requestCapture}
      t={t}
    />,
  ];

  return (
    <FormProvider {...formMethods}>
      <LockCalculatorCatalogProvider isAdmin={isAdmin} calculationId={data?.id}>
        <NoticesProvider value={noticesContext}>
        <FlexContent padding noPaddingTop className={styles.pageFlex}>
          <div className={styles.pageShell} style={{ "--accent-base": accentColor } as CSSProperties}>
            {/* ═══════════ Form (left) + 3D preview and results (right) ═══════════ */}
            <div className={classNames(styles.workspace, { [styles.workspaceFormCollapsed]: isFormCollapsed })}>
              {/* ── Left pane: the wizard, or just the rail once collapsed ── */}
              <div className={styles.formPane}>
                <button
                  type="button"
                  className={styles.formRail}
                  aria-expanded={false}
                  title={t.common.show}
                  onClick={() => setIsFormCollapsed(false)}
                >
                  <ArrowDropDownIcon className={styles.formRailChevron} />
                  <span className={styles.formRailTitle}>{t.lockCalculatorModule.tabs.designInput}</span>
                </button>

                <div className={styles.formColumn}>
                  <button
                    type="button"
                    className={styles.formToggle}
                    aria-expanded
                    title={t.common.hide}
                    onClick={() => setIsFormCollapsed(true)}
                  >
                    <ArrowDropDownIcon className={styles.formToggleChevron} />
                    <span className={styles.formToggleTitle}>{t.lockCalculatorModule.tabs.designInput}</span>
                  </button>

                  <CalculatorWizard
                    t={t}
                    readOnly={readOnly}
                    isAdmin={isAdmin}
                    currentStep={currentStep}
                    goToStep={goToStep}
                    onOpenHelp={onOpenHelp}
                    connectorsEnabled={!!lockCalculatorCalculation?.configuration}
                    onTriggerOperation={triggerOperation}
                    isTriggeringOperation={isTriggeringOperation}
                    coefficients={coefficients}
                  />
                </div>
              </div>

              {/* ── Right column: collapsible 3D preview, then the results tabs ── */}
              <div className={styles.resultsColumn}>
                <ExpansionPanel
                  title={t.lockCalculatorModule.preview3d}
                  expanded={isPreviewExpanded}
                  onToggle={() => setIsPreviewExpanded((prev) => !prev)}
                  className={styles.previewPanel}
                >
                  <LockCalculator3DViewer
                    fastenerSpecs={fastenerSpecs}
                    connectorCodes={connectorCodes}
                    headerLockCode={headerLockCode}
                    serviceClass={serviceClass}
                    preDrillingLines={preDrillingLines}
                    calculationLegend={calculationLegend}
                    lockstopInstallationLevels={lockstopInstallationLevels}
                    lockstopCode={lockstopCode}
                    screwEdgeThicknessMm={screwEdgeThicknessMm}
                    topDistanceBase={topDistanceBase}
                  />
                </ExpansionPanel>

                <div className={styles.resultsTabs}>
                  <NoticesPanel />
                  <Tabs renderMode="eager"
                    urlParameter={RESULTS_TAB_PARAMETER}
                    tabs={[
                      { title: `${t.lockCalculatorModule.generatedConfigurations.title} (${generatedCount})` },
                      { title: t.lockCalculatorModule.tabs.calculationComparison },
                      { title: t.lockCalculatorModule.tabs.bom },
                      { title: t.lockCalculatorModule.tabs.report },
                    ]}
                    contents={contents}
                  />
                </div>
              </div>
            </div>

            {/* key = firma: una scena cambiata a meta' cattura riparte da zero
                invece di mescolare viste vecchie e nuove. */}
            {hasResults && captureRequested && !capturedViews && captureSignature === sceneSignature && (
              <div style={{ position: "fixed", top: 0, left: 0, width: 1200, height: 450, visibility: "hidden", pointerEvents: "none" }}>
                <LockSceneCapture
                  key={captureSignature}
                  fastenerSpecs={fastenerSpecs}
                  lockstopInstallationLevels={lockstopInstallationLevels}
                  lockstopCode={lockstopCode}
                  screwEdgeThicknessMm={screwEdgeThicknessMm}
                  topDistanceBase={topDistanceBase}
                  onCapture={handleCaptured}
                />
              </div>
            )}

            <HelpModal content={helpModal} onClose={() => setHelpModal(null)} t={t} />
          </div>
        </FlexContent>
        </NoticesProvider>
      </LockCalculatorCatalogProvider>
    </FormProvider>
  );
};

export default LockCalculator;
